import * as THREE from 'three';
import type { World } from '../world/World';
import type { Player } from '../player/Player';
import { itemDef } from '../data/items';
import { resourceDefs, type ResourceDef } from '../data/resources';
import { mulberry32, hashString, randInt } from '../core/Rng';
import type { ResourceInstance } from '../world/WorldGen';

/**
 * Kaynak toplama.
 * Doğru alet hızlı toplar, yanlış alet çok yavaş; sertlik yetmiyorsa hiç kırılmaz.
 * Düşen eşyalar doğrudan envantere gider (mobilde yerden toplama zahmeti olmasın),
 * envanter dolarsa uyarı verilir ve kaynak korunur.
 */

export interface HarvestTarget {
  instance: ResourceInstance;
  def: ResourceDef;
  distance: number;
  point: THREE.Vector3;
}

export interface HarvestFeedback {
  kind: 'hit' | 'broken' | 'blocked' | 'full' | 'none';
  message?: string;
  drops?: Array<{ item: string; count: number }>;
  target?: HarvestTarget;
}

const REACH = 4.6;
const raycaster = new THREE.Raycaster();
raycaster.far = REACH;

/** Nişan alınan kaynağı bulur. */
export function pickResource(world: World, player: Player): HarvestTarget | null {
  const origin = player.eyePosition;
  const dir = player.forward();
  raycaster.set(origin, dir);
  const targets: THREE.Object3D[] = [];
  for (const c of world.loadedChunks()) targets.push(...c.pickables());
  const hits = raycaster.intersectObjects(targets, false);
  for (const hit of hits) {
    const mesh = hit.object as THREE.InstancedMesh;
    if (hit.instanceId === undefined || !mesh.isInstancedMesh) continue;
    const chunk = world.loadedChunks().find((c) => c.pickables().includes(mesh));
    if (!chunk) continue;
    const inst = chunk.resourceByInstance(mesh, hit.instanceId);
    if (!inst) continue;
    const def = resourceDefs.get(inst.def);
    if (!def) continue;
    return { instance: inst, def, distance: hit.distance, point: hit.point.clone() };
  }
  return null;
}

/** Bir vuruşun kaynağa verdiği hasar; 0 = kırılamaz. */
export function hitPower(player: Player, def: ResourceDef): number {
  const held = player.inventory.held();
  const tool = held && !held.broken ? itemDef(held.item).tool : null;
  const level = tool?.harvestLevel ?? 0;
  if (def.hardness > 0 && level < def.hardness) return 0;

  const correct = tool && def.tool && tool.class === def.tool;
  const base = correct ? 12 * (tool!.speed ?? 1) : def.tool === null ? 10 : 2.2;
  const mining = def.kind === 'ore' || def.kind === 'rock' ? player.stats.modifier('mining') : 1;
  return base * mining;
}

/** İlerleme durumu: aynı kaynağa vuruşlar birikir. */
const progress = new Map<string, number>();

export function harvest(world: World, player: Player, gameTime: number): HarvestFeedback {
  const target = pickResource(world, player);
  if (!target) return { kind: 'none' };
  if (world.isHarvested(target.instance.id, gameTime)) return { kind: 'none' };

  const power = hitPower(player, target.def);
  if (power <= 0) {
    const needed = target.def.hardness;
    return { kind: 'blocked', message: `Daha güçlü bir kazma gerekiyor (seviye ${needed}).`, target };
  }

  const id = target.instance.id;
  const cur = (progress.get(id) ?? 0) + power;

  if (cur < target.def.health) {
    player.inventory.wearHeld(Math.max(1, Math.round(world.params.difficulty.durabilityWear)));
    progress.set(id, cur);
    return { kind: 'hit', target };
  }

  // Kırıldı: loot hesapla
  progress.delete(id);
  const rand = mulberry32(hashString(id) ^ world.params.seed);
  const mul = world.params.difficulty.resourceYield;
  const drops: Array<{ item: string; count: number }> = [];
  for (const d of target.def.drops) {
    if (rand() > d.chance) continue;
    const count = Math.max(1, Math.round(randInt(rand, d.min, d.max) * mul));
    drops.push({ item: d.item, count });
  }

  // Envanterde yer yok ise kaynağı harcamayız
  const totalNeeded = drops.reduce((n, d) => n + (itemDef(d.item).stack === 1 ? d.count : 0), 0);
  if (totalNeeded > player.inventory.freeSlots()) {
    return { kind: 'full', message: 'Çantan dolu!', target };
  }

  // Başarılı vuruş artık gerçekten gerçekleşti: tam envanterde dayanıklılık
  // kaybetme yok. Kısmi vuruşlar da aşağıdaki ile aynı maliyeti öder.
  const wear = Math.max(1, Math.round(world.params.difficulty.durabilityWear));
  player.inventory.wearHeld(wear);

  let anyLeft = false;
  for (const d of drops) {
    const left = player.inventory.add(d.item, d.count);
    if (left > 0) anyLeft = true;
  }
  world.apply({ t: 'resource:harvest', id, def: target.def.id });
  void gameTime;
  return {
    kind: 'broken',
    drops,
    target,
    message: anyLeft ? 'Çanta doldu, bir kısmı yerde kaldı.' : undefined,
  };
}

/** Kırılma ilerlemesi 0..1 (UI çubuğu için). */
export function harvestProgress(id: string, def: ResourceDef): number {
  return Math.min(1, (progress.get(id) ?? 0) / def.health);
}

export function clearProgress(): void {
  progress.clear();
}
